import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { parseTTRCode } from '../../utils/ttr/parseTTRCode';
import { VerificationDoc, VerifyResult, ScannedTTR } from './types';

/**
 * Resolve a scanned TTR code to a publicVerification mirror doc (Â§7.1 logic):
 *
 *   1. parseTTRCode() on the URL param.
 *   2. Exact match on publicVerification/{full-ttrCode} â†’ use if found.
 *   3. On miss, find the canonical position record by parsed.boardId +
 *      parsed.position. We use a single-field equality query on parsed.boardId
 *      (no composite index needed) and match parsed.position client-side.
 *   4. Either way, the SCANNED condition/holder/copy are returned separately so
 *      the page can show them prominently while rendering canonical position data.
 *
 * Anonymous read â€” publicVerification has `allow read: if true`. No auth.
 */
export function useVerification(rawTtrCode: string | undefined): VerifyResult {
  const [result, setResult] = useState<VerifyResult>({
    status: 'loading',
    doc: null,
    scanned: null,
    matchType: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      // 1. parse
      let scanned: ScannedTTR;
      try {
        const p = parseTTRCode(rawTtrCode || '');
        scanned = {
          ttrCode: p.ttrCode,
          boardId: p.boardId,
          position: p.position,
          condition: p.condition,
          holder: p.holder,
          copy: p.copy,
        };
      } catch {
        if (!cancelled) setResult({ status: 'invalid', doc: null, scanned: null, matchType: null });
        return;
      }

      try {
        // 2. exact match
        const exactSnap = await getDoc(doc(db, 'publicVerification', scanned.ttrCode));
        if (exactSnap.exists()) {
          if (!cancelled) {
            setResult({
              status: 'ok',
              doc: exactSnap.data() as VerificationDoc,
              scanned,
              matchType: 'exact',
            });
          }
          return;
        }

        // 3. fallback by boardId + position (canonical position record)
        const wantPosition = String(parseInt(scanned.position, 10)).padStart(3, '0');
        const q = query(
          collection(db, 'publicVerification'),
          where('parsed.boardId', '==', scanned.boardId),
          limit(50),
        );
        const qSnap = await getDocs(q);
        let canonical: VerificationDoc | null = null;
        qSnap.forEach((d) => {
          if (canonical) return;
          const data = d.data() as VerificationDoc;
          if (data.parsed && data.parsed.position === wantPosition) canonical = data;
        });

        if (canonical) {
          if (!cancelled) setResult({ status: 'ok', doc: canonical, scanned, matchType: 'fallback' });
          return;
        }

        // valid code, but no mirror doc yet (race: sticker in the wild, mirror not populated)
        if (!cancelled) setResult({ status: 'not_found', doc: null, scanned, matchType: null });
      } catch (err) {
        if (!cancelled) setResult({ status: 'error', doc: null, scanned, matchType: null });
      }
    }

    setResult({ status: 'loading', doc: null, scanned: null, matchType: null });
    resolve();
    return () => {
      cancelled = true;
    };
  }, [rawTtrCode]);

  return result;
}
